import BaseQuestionModel from '../BaseQuestionModel'
import { IQuestionModelConstructor, QuestionType } from '../types'
import {
  EMPTY_RESPONSE,
  EMPTY_SUBMIT_RESPONSE,
  IProgressiveMatrixQuestionModelState,
  IProgressiveMatrixQuestionModelTypes,
  IProgressiveMatrixResponseValidation
} from './types'

const ProgressiveMatrixQuestionModel: IQuestionModelConstructor<IProgressiveMatrixQuestionModelTypes> = (
  questionSchema,
  action
) => {
  const { model } = BaseQuestionModel<IProgressiveMatrixQuestionModelTypes, IProgressiveMatrixQuestionModelState>({
    questionType: QuestionType.PROGRESSIVE_MATRIX,
    questionSchema: questionSchema,
    action,
    submitResponse,
    initState,
    validateResponse
  })
  return model
}

function submitResponse({ response }: IProgressiveMatrixQuestionModelState) {
  return response.data.reduce(
    (prev, cur) => {
      cur
        .filter(a => a.selected && (a.enabled || response.split) && (a.oe != 'mandatory' || a.comment?.length))
        .forEach(a => {
          if (a.na || !cur.some(b => b.na && b.selected && b.branchId == a.branchId))
            prev.push(a.comment ? { id: a.id, c: a.comment } : { id: a.id })
        })

      return prev
    },
    [...EMPTY_SUBMIT_RESPONSE]
  )
}

function initState(state: IProgressiveMatrixQuestionModelState): IProgressiveMatrixQuestionModelState {
  const { questionSchema } = state
  const response = { data: [...EMPTY_RESPONSE], split: false }

  questionSchema.matrix_batches
    .filter(batch => (questionSchema.split ? !batch.hidden && !batch.fakeHidden : true))
    .forEach((batch, bi) => {
      response.data[bi] = []
      batch.answers.forEach((answer, aj) => {
        response.data[bi][aj] = {
          branchId: batch.id,
          id: answer.id,
          selected: false,
          oe: answer.oe,
          na: answer.na,
          text: 'rows' == questionSchema.flow ? batch.text : answer.text,
          enabled: questionSchema.progressive_flow ? !bi : true,
          x: bi,
          y: aj
        }
      })
    })

  state.response = response

  return state
}

function validateMono({
  questionSchema: questionSchema,
  response,
  frame
}: IProgressiveMatrixQuestionModelState): IProgressiveMatrixResponseValidation {
  const invalidBatches: number[] = []
  const minChoices =
    (questionSchema.global_min_choices
      ? Math.max(questionSchema.global_min_choices, questionSchema.min_choices)
      : questionSchema.min_choices) || 0
  response.data.forEach((batch, bj) => {
    const checkedNum = batch.filter(a => a.selected && (a.oe != 'mandatory' || a.comment?.trim().length)).length
    const curNaAnswers = batch.filter(
      (a, aj) => a.selected && questionSchema.matrix_batches[bj]?.answers[aj]?.na
    ).length
    if (
      bj <= (frame as number) &&
      ('rb' == questionSchema.subtype
        ? checkedNum != 1 && !questionSchema.matrix_batches[bj]?.fakeHidden
        : (minChoices > checkedNum || checkedNum > (questionSchema.max_choices || 100)) && !curNaAnswers)
    )
      invalidBatches.push(bj)
  })

  if ('rb' == questionSchema.subtype)
    return {
      valid: !response.data.some(
        (batch, bj) =>
          1 != batch.filter(a => a.selected && (a.oe != 'mandatory' || a.comment?.trim().length)).length &&
          !questionSchema.matrix_batches[bj].fakeHidden &&
          !questionSchema.matrix_batches[bj].hidden
      ),
      currentValid: response.data.reduce(
        (result, batch, bj) =>
          (bj > (frame as number) ||
            1 == batch.filter(a => a.selected && (a.oe != 'mandatory' || a.comment?.trim().length)).length ||
            questionSchema.matrix_batches[bj].fakeHidden ||
            questionSchema.matrix_batches[bj].hidden) &&
          result,
        true
      ),
      invalidBatches: invalidBatches
    }

  if ('cb' == questionSchema.subtype) {
    return !questionSchema.progressive_flow
      ? {
          valid: true
        }
      : {
          valid: response.data.reduce((result, batch, bi) => {
            const curAnswers = batch.filter(a => a.selected && (a.oe != 'mandatory' || a.comment?.trim().length)).length
            const curNaAnswers = batch.filter(
              (a, aj) => a.selected && questionSchema.matrix_batches[bi].answers[aj].na
            ).length

            return (
              (bi > (frame as number) ||
                curNaAnswers > 0 ||
                questionSchema.matrix_batches[bi].fakeHidden ||
                questionSchema.matrix_batches[bi].hidden ||
                (minChoices <= curAnswers && curAnswers <= (questionSchema.max_choices || 100))) &&
              result
            )
          }, true),
          invalidBatches: invalidBatches
        }
  }

  return {
    valid: false
  }
}

function validateSplited({
  questionSchema: questionSchema,
  response,
  frame
}: IProgressiveMatrixQuestionModelState): IProgressiveMatrixResponseValidation {
  const currentResponse = response.data[frame as number]
  if (!currentResponse)
    return {
      valid: false
    }

  const naSelected = currentResponse.some(a => a.selected && a.na)
  const selected = currentResponse.filter(
    a => !a.na && a.selected && (a.oe != 'mandatory' || a.comment?.trim().length)
  ).length
  const curBadAnswers = currentResponse.filter(
    a => a.selected && 'mandatory' == a.oe && !a.comment?.trim().length
  ).length

  if (
    'cb' == questionSchema.subtype &&
    (naSelected || ((questionSchema.min_choices || 0) <= selected && (questionSchema.max_choices || 5000) >= selected))
  )
    return {
      valid: !curBadAnswers || naSelected
    }

  if ('rb' == questionSchema.subtype && 1 == selected)
    return {
      valid: true
    }

  return {
    valid: false
  }
}

function validateResponse({
  questionSchema: questionSchema,
  response,
  frame
}: IProgressiveMatrixQuestionModelState): IProgressiveMatrixResponseValidation {
  if (
    !questionSchema.matrix_batches.filter(b => !b.hidden).length ||
    !questionSchema.matrix_batches[0].answers.filter(b => !b.hidden).length
  )
    return {
      valid: true
    }

  return response.split
    ? validateSplited({ questionSchema, response, frame } as IProgressiveMatrixQuestionModelState)
    : validateMono({ questionSchema, response, frame } as IProgressiveMatrixQuestionModelState)
}

export default ProgressiveMatrixQuestionModel
